import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { capitalize } from '../../../../utils/capitalize-string';
import { Icon } from '@purpurds/purpur/icon';
import { icons } from 'theme/icons';
import NOB from '../../../../constants/language-strings';
import Status, { COMPLETE, INCOMPLETE } from '../../../../constants/status-codes';
import './workOrderDetails.scss';
import { getHours, getMinutes } from '../../../../utils/time-utils/time-calculator';
import { isPast } from 'utils/past-due-date';
import { formatDate } from 'utils/format-date/format-date';
const WorkOrderDetails = ({ travelDuration = 0, workDuration = 0, isFetching = false, done, workOrder, totalTimeEstimate, inWorkLog, }) => {
    const [driveSeconds, setDriveSeconds] = useState(travelDuration);
    const [workSeconds, setWorkSeconds] = useState(workDuration);
    const driveTimerRef = useRef(null);
    const workTimerRef = useRef(null);
    const isTraveling = Status.TRAVELING.includes(workOrder.status);
    const isOnSite = Status.ON_SITE.includes(workOrder.status);
    const formatTime = (sec) => {
        const hours = Math.floor(sec / 3600);
        const minutes = Math.floor((sec % 3600) / 60);
        const seconds = Math.ceil(sec % 60);
        return {
            hours: hours.toString(),
            minutes: minutes.toString(),
            seconds: seconds.toString(),
        };
    };
    const [driveTime, setDriveTime] = useState(formatTime(travelDuration - workDuration));
    const [workTime, setWorkTime] = useState(formatTime(workDuration));
    const startDriveTimer = () => {
        if (!driveTimerRef.current) {
            driveTimerRef.current = setInterval(() => {
                setDriveSeconds(prev => {
                    const newVal = prev + 1;
                    setDriveTime(formatTime(newVal));
                    return newVal;
                });
            }, 1000);
        }
    };
    const stopDriveTimer = () => {
        if (driveTimerRef.current) {
            clearInterval(driveTimerRef.current);
            driveTimerRef.current = null;
        }
    };
    const startWorkTimer = () => {
        if (!workTimerRef.current) {
            workTimerRef.current = setInterval(() => {
                setWorkSeconds(prev => {
                    const newVal = prev + 1;
                    setWorkTime(formatTime(newVal));
                    return newVal;
                });
            }, 1000);
        }
    };
    const stopWorkTimer = () => {
        if (workTimerRef.current) {
            clearInterval(workTimerRef.current);
            workTimerRef.current = null;
        }
    };
    useEffect(() => {
        if (isTraveling) {
            setDriveSeconds(travelDuration);
            setDriveTime(formatTime(travelDuration));
            startDriveTimer();
            stopWorkTimer();
        }
        if (isOnSite) {
            setWorkSeconds(workDuration);
            setDriveSeconds(travelDuration);
            setDriveTime(formatTime(travelDuration));
            startWorkTimer();
            stopDriveTimer();
        }
    }, []);
    useEffect(() => {
        const actualDrive = travelDuration !== 0 ? travelDuration : driveSeconds;
        if (isTraveling) {
            setDriveSeconds(actualDrive);
            setDriveTime(formatTime(actualDrive));
            startDriveTimer();
            stopWorkTimer();
        }
        if (isOnSite) {
            setWorkSeconds(workDuration);
            setDriveSeconds(actualDrive);
            setWorkTime(formatTime(workDuration));
            setDriveTime(formatTime(actualDrive));
            startWorkTimer();
            stopDriveTimer();
        }
    }, [travelDuration, workDuration, workOrder.status]);
    useEffect(() => {
        return () => {
            stopWorkTimer();
            stopDriveTimer();
        };
    }, []);
    const driveTimer = `${driveTime.hours.padStart(2, '0')}:${driveTime.minutes.padStart(2, '0')}:${driveTime.seconds.padStart(2, '0')}`;
    const workTimer = `${workTime.hours.padStart(2, '0')}:${workTime.minutes.padStart(2, '0')}:${workTime.seconds.padStart(2, '0')}`;
    const status = /\d/.test(workOrder.status)
        ? workOrder.status.slice(workOrder.status.lastIndexOf(' ') + 1)
        : workOrder.status;
    const header = () => `${NOB.WORKORDER_DETAILS.ESTIMATED_TIME} ${getHours(totalTimeEstimate)} ${getMinutes(totalTimeEstimate)}`;
    const isPastDue = isPast(workOrder.serviceDate);
    return (_jsxs("div", { className: "workOrder__details__outer__container", children: [_jsxs("div", { className: "workOrder__details__container", children: [_jsxs("span", { className: "item", children: [_jsxs("span", { className: "item__description", children: [NOB.WORKORDER_DETAILS.WORKORDER, ":\u00A0"] }), workOrder.workOrderId] }), !(isOnSite || isTraveling) ? (_jsx("div", { children: COMPLETE.includes(status) || INCOMPLETE.includes(status) ? (_jsx("div", { className: "item", children: _jsx("span", { className: "item--item", children: capitalize(status) }) })) : (_jsxs("div", { className: isPastDue ? 'item--red' : 'item', children: [formatDate(workOrder.serviceDate, 'short'), " kl.", workOrder.timeSlot] })) })) : (_jsx("div", { className: "item", children: _jsx("span", { children: isTraveling
                                ? NOB.WORKORDER_LIST.DRIVESTARTED
                                : NOB.WORKORDER_LIST.WORKSTARTED }) }))] }), !done && !inWorkLog && (_jsx("div", { children: !(isOnSite || isTraveling) ? (_jsx("span", { className: "workOrder__details__container__items--estimatedTime", children: header() })) : (_jsxs("div", { className: "workOrder__details__container--bottom", children: [isOnSite && (_jsxs("div", { className: "workOrder__details__container__items--started", children: [_jsx(Icon, { svg: icons.repair }), _jsx("div", { children: NOB.WORKORDER_DETAILS.WORKING_TIME }), "\u00A0", isFetching ? (_jsx("div", { className: "loader__wrapper", children: _jsx("div", { className: "circle-loader-black" }) })) : (_jsx("span", { children: workTimer }))] })), _jsxs("div", { className: isTraveling
                                ? 'workOrder__details__container__items--started'
                                : 'workOrder__details__container__items--bottom', children: [_jsx(Icon, { svg: icons.delivery }), NOB.WORKORDER_DETAILS.DRIVING_TIME, "\u00A0", isFetching ? (_jsx("div", { className: "loader__wrapper", children: _jsx("div", { className: "circle-loader-black" }) })) : (_jsx("span", { children: driveTimer }))] })] })) }))] }));
};
export default WorkOrderDetails;
